import { useCallback, useEffect, useMemo, useState } from "react";
import styled from "@emotion/styled";
import {
    HeaderBaseProps,
    SearchResults as BaseSearchResults,
    SearchResultsResponse,
} from "@kaltura/mediaspace-shared-media-list";
import { SearchResultsInnerComponentProps } from "@kaltura/mediaspace-shared-search";
import { BulkActionMenuItemInjectedProps, FilterValues, KmsTypeComponent } from "@kaltura/mediaspace-shared-types";
import { BulkActionProps } from "./types";
import { ResultsHeader } from "./results-header/ResultsHeader";
import MediaCard from "../../../media-card/MediaCard";

export interface SearchResultsProps extends SearchResultsResponse, SearchResultsInnerComponentProps {
    bulkButtonActions?: KmsTypeComponent<any, BulkActionProps>[];
    bulkMenuActions?: KmsTypeComponent<any, BulkActionMenuItemInjectedProps>[];
    onInvalidate?: () => void;
    onAllSelected?: (checked: boolean) => void;
    additionalInvalidation?: () => void;
    filterValues?: FilterValues;
    areSearchResultsDraggable?: boolean;
}

const StyledSearchResultsWrapper = styled(
    "div",
    {
        shouldForwardProp(propName: PropertyKey): boolean {
            return propName !== "isWideLayout";
        },
    }
)<{ isWideLayout?: boolean }>(({ isWideLayout, theme }) => ({
    [theme.breakpoints.down("lg")]: {
        marginTop: isWideLayout ? undefined : theme.spacing(3),
    },
    [theme.breakpoints.down("md")]: {
        marginTop: theme.spacing(3),
    },
}));

/**
 * Selectable Search Results - wraps base SearchResults with selection functionality
 */
export function SearchResults(props: SearchResultsProps) {
    const {
        entryResults,
        page = 1,
        onPageChange,
        pageSize,
        onEntryShowMore,
        isWideLayout,
        isTextSearch,
        searchInLabel,
        bulkButtonActions,
        bulkMenuActions,
        onInvalidate,
        onAllSelected,
        additionalInvalidation,
        filterValues,
        areSearchResultsDraggable = false,
    } = props;
    const [selectedEntries, setSelectedEntries] = useState<string[]>([]);

    const handleSelectAllClick = (checked: boolean) => {
        onAllSelected?.(checked);

        if (!checked) {
            // unselect all
            setSelectedEntries([]);
            return;
        }
        // select all
        const ar = [];
        for (const o of entryResults?.objects ?? []) {
            ar.push(o.object.id);
        }
        setSelectedEntries(ar);
    };

    const handleSelectionChanged = (entryId: string, selected: boolean) => {
        if (selected) {
            // add the entry
            setSelectedEntries((oldVal) => {
                return [...oldVal, entryId];
            });
        }
        else {
            // remove the entry
            setSelectedEntries((oldVal) => {
                return oldVal.filter((v) => v !== entryId);
            });
        }
    };

    const handleInvalidate = () => {
        // reset selections
        setSelectedEntries([]);

        // reset to first page
        if (page !== 1) {
            onPageChange(1);
        }

        // update container
        onInvalidate?.();
        additionalInvalidation?.();
    };

    useEffect(() => {
        // when media list updates, reset all local selections
        setSelectedEntries([]);
    }, [entryResults]);

    // Convert selectedEntries array to Set for O(1) lookup performance
    const selectedEntriesSet = useMemo(() => new Set(selectedEntries), [selectedEntries]);

    // Stable callback to check if an entry is selected
    const isSelected = useCallback((entryId: string) => selectedEntriesSet.has(entryId), [selectedEntriesSet]);

    // Create custom header component with selection functionality
    const selectableHeader: KmsTypeComponent<any, HeaderBaseProps> = {
        component: ResultsHeader,
        name: "selectableMediaListResultsHeader",
        props: {
            entryResults,
            searchInLabel,
            bulkButtonActions,
            bulkMenuActions,
            filterValues,
            onAllSelected: handleSelectAllClick,
            onReload: handleInvalidate,
            selectedEntries,
        },
    };

    // Create custom media card with selection support
    const selectableMediaCard: KmsTypeComponent = {
        component: MediaCard,
        name: "selectableMediaCard",
        props: {
            isSelected,
            onChange: handleSelectionChanged,
        },
    };

    // Callback to customize drag behavior for multi-selection
    const getDragData = (entryId: string) => {
        if (!areSearchResultsDraggable) {
            return { idsToDrag: [], dragItemsCount: 0 };
        }
        const isEntrySelected = selectedEntries.includes(entryId);
        const shouldDragMultiple = isEntrySelected && selectedEntries.length > 1;
        const idsToDrag = shouldDragMultiple ? selectedEntries : [entryId];
        const dragItemsCount = idsToDrag.length;

        return { idsToDrag, dragItemsCount };
    };

    return (
        <StyledSearchResultsWrapper isWideLayout={isWideLayout}>
            <BaseSearchResults
                entryResults={entryResults}
                page={page}
                onPageChange={onPageChange}
                pageSize={pageSize}
                onEntryShowMore={onEntryShowMore}
                isWideLayout={isWideLayout}
                isTextSearch={isTextSearch}
                onInvalidate={handleInvalidate}
                additionalInvalidation={additionalInvalidation}
                filterValues={filterValues}
                areSearchResultsDraggable={areSearchResultsDraggable}
                header={selectableHeader}
                mediaCard={selectableMediaCard}
                getDragData={getDragData}
            />
        </StyledSearchResultsWrapper>
    );
}

export default SearchResults;
